import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getMe, logout } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import {
  ROOT_ITEMS,
  SECTIONS,
  matchesItem,
  sectionForPath,
  sectionHome,
  type NavItem,
} from "@/lib/nav";

/**
 * The primary rail: cross-cutting tools, then the five sections. Picking a
 * section takes you into it and its pages appear in the second column
 * (SectionNav) — the rail itself stays one level deep, so it never becomes
 * a wall of nested links.
 *
 * Shut by default, showing icons only, and opens on hover or keyboard focus
 * — the same behaviour as the PE platform's rail. IT OPENS OVER THE PAGE,
 * NOT INTO IT: the layout only ever reserves the shut width
 * (`--sidebar-width` in index.css), so a pointer wandering past never makes
 * the page reflow under you.
 */

const WIDTH_OPEN = 234;
/** A pointer crossing the rail on its way somewhere else shouldn't open it. */
const OPEN_DELAY_MS = 120;

/**
 * Every glyph stands on the shut rail's centreline, open or shut, so opening
 * reveals labels beside icons that haven't moved. Derived from the token, so
 * changing the shut width can't knock the column off-centre.
 */
const onCentre = (width: number, gutter = 0) =>
  `calc(var(--sidebar-width) / 2 - ${width / 2 + gutter}px)`;
/** Nav icons are `w-4`, the avatar 28px, the brand mark `h-7 w-7`. */
const ICON = 16;
const AVATAR = 28;
const MARK = 28;
/** The padding the nav rows stand in. */
const GUTTER = 12;

/**
 * Labels are hidden, never unmounted: a label sets its row's height, so
 * dropping it would shift every icon up as the rail shut.
 */
const label = (shut: boolean) => `truncate ${shut ? "invisible" : ""}`;

const linkCls = (isActive: boolean) =>
  [
    "mb-0.5 flex items-center gap-3 rounded-md py-2 pr-3 text-sm font-medium transition-colors",
    isActive
      ? "bg-accent-muted text-text-heading"
      : "text-text-secondary hover:bg-bg-card-hover hover:text-text-heading",
  ].join(" ");

const rowStyle = { paddingLeft: onCentre(ICON, GUTTER) };

function RailLink({
  to,
  icon,
  text,
  isActive,
  shut,
}: {
  to: string;
  icon: string;
  text: string;
  isActive: boolean;
  shut: boolean;
}) {
  return (
    <NavLink
      to={to}
      title={shut ? text : undefined}
      className={linkCls(isActive)}
      style={rowStyle}
    >
      <i className={`fa-solid ${icon} w-4 shrink-0 text-center text-[13px]`} />
      <span className={label(shut)}>{text}</span>
    </NavLink>
  );
}

function RootLink({ item, shut }: { item: NavItem; shut: boolean }) {
  const { pathname } = useLocation();
  return (
    <RailLink
      to={item.to}
      icon={item.icon}
      text={item.label}
      isActive={matchesItem(item, pathname)}
      shut={shut}
    />
  );
}

export function Sidebar() {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const active = sectionForPath(pathname);

  const [open, setOpen] = useState(false);
  const openTimer = useRef<number | null>(null);
  const cancelOpen = () => {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };
  const armOpen = () => {
    cancelOpen();
    openTimer.current = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  };
  const close = () => {
    cancelOpen();
    setOpen(false);
  };
  useEffect(() => cancelOpen, []);
  // Once you've picked somewhere to go, get out of the way — on a touch
  // screen there's no pointer leaving to close it.
  useEffect(close, [pathname]);

  const shut = !open;

  async function onLogout() {
    await logout().catch(() => {});
    qc.clear();
    navigate("/login", { replace: true });
  }

  return (
    // Holds the shut width in the layout; the rail overlays the page from here.
    <div className="relative h-full shrink-0" style={{ width: "var(--sidebar-width)" }}>
      <aside
        className={`sidebar-green absolute inset-y-0 left-0 z-30 flex flex-col overflow-x-hidden border-r border-border bg-bg-secondary transition-[width,box-shadow] duration-200 ${
          open ? "shadow-token-xl" : ""
        }`}
        style={{ width: open ? WIDTH_OPEN : "var(--sidebar-width)" }}
        onMouseEnter={armOpen}
        onMouseLeave={close}
        // Keyboard users tab into the rail and get the same reveal; tabbing
        // out shuts it. `relatedTarget` is null when focus leaves the page.
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close();
        }}
      >
        {/* Brand */}
        <div
          className="flex h-16 shrink-0 items-center gap-2.5 border-b border-border pr-5"
          style={{ paddingLeft: onCentre(MARK) }}
        >
          <img src="/logo.png" alt="Vernias" className="h-7 w-7 shrink-0 object-contain" />
          <span
            className={`whitespace-nowrap font-display text-[15px] font-semibold text-text-heading ${
              shut ? "invisible" : ""
            }`}
          >
            Vernias Portal
          </span>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4">
          {ROOT_ITEMS.map((item) => (
            <RootLink key={item.to} item={item} shut={shut} />
          ))}

          <div className="mx-3 my-3 border-t border-border" />

          {SECTIONS.map((section) => (
            <RailLink
              key={section.key}
              to={sectionHome(section)}
              icon={section.icon}
              text={section.label}
              isActive={active?.key === section.key}
              shut={shut}
            />
          ))}
        </nav>

        {/* Footer: settings, who you are, sign out */}
        <div className="border-t border-border px-3 py-3">
          <RailLink
            to="/settings"
            icon="fa-gear"
            text="Settings"
            isActive={pathname === "/settings"}
            shut={shut}
          />
          <div
            className="mt-1 flex items-center gap-2.5 py-2 pr-3"
            style={{ paddingLeft: onCentre(AVATAR, GUTTER) }}
          >
            {/* A fixed box, so the image's `max-width: 100%` can't squeeze
                the avatar into an oval while the rail is narrow. */}
            <span className="shrink-0" style={{ width: AVATAR, height: AVATAR }}>
              <Avatar name={me?.name ?? "?"} url={me?.avatarUrl} size={AVATAR} />
            </span>
            <div className={`min-w-0 flex-1 ${shut ? "invisible" : ""}`}>
              <div className="truncate text-xs font-medium text-text-heading">
                {me?.name ?? "…"}
              </div>
              <div className="truncate text-[11px] text-text-muted">{me?.role ?? ""}</div>
            </div>
          </div>
          {/* Its own row, so it sits on the centreline with every other
              icon rather than jumping from under the avatar to the far edge
              as the rail opens. */}
          <button
            type="button"
            onClick={onLogout}
            title={shut ? "Sign out" : undefined}
            className={`${linkCls(false)} w-full`}
            style={rowStyle}
          >
            <i className="fa-solid fa-arrow-right-from-bracket w-4 shrink-0 text-center text-[13px]" />
            <span className={label(shut)}>Sign out</span>
          </button>
        </div>
      </aside>
    </div>
  );
}
